import React, { useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import CourseForm from '../../../components/forms/CourseForm';
import AdminLayout from '../../../components/layout/AdminLayout';
import { useCreateCourse } from '../../../hooks/useCourseApi';
import {
  useApproveCourseRequest,
  useCourseRequestById,
} from '../../../hooks/useCourseRequestApi';
import { useFileApi } from '../../../hooks/useFileApi';
import { CourseDTO } from '../../../services/apiClient';
import { showToast } from '../../../utils/showToast';

const CourseCreate: React.FC = () => {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [headerImageFile, setHeaderImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createCourse = useCreateCourse();
  const { uploadFile } = useFileApi();
  const navigate = useNavigate();

  // Reached from the course request review screen with ?fromRequest=<id>. The form opens with what
  // the member told us, and saving the course approves that request.
  const [searchParams] = useSearchParams();
  const fromRequestId = searchParams.get('fromRequest');
  const { data: courseRequest, isLoading: isLoadingRequest } =
    useCourseRequestById(fromRequestId);
  const approveRequest = useApproveCourseRequest();

  const backTo = fromRequestId
    ? `/admin/course-requests/${fromRequestId}`
    : '/admin/courses';

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    fromRequestId
      ? { label: 'Course Requests', href: '/admin/course-requests' }
      : { label: 'Courses', href: '/admin/courses' },
    { label: 'Create', href: '#' },
  ];

  // Only what maps cleanly. Older requests carry a free-text "town or area" instead of a state,
  // so that is shown above the form rather than dropped into Address, which is meant to come
  // from the Mapbox lookup.
  const initialValues = {
    name: courseRequest?.courseName,
    countryId: courseRequest?.countryId,
    stateId: courseRequest?.stateId,
    websiteUrl: courseRequest?.websiteUrl,
  } as CourseDTO;

  const handleSubmit = async (data: CourseDTO): Promise<void> => {
    try {
      setIsSubmitting(true);

      // One shared "files" container, one folder per purpose. This matches the live production
      // storage account, which already holds "files/course-logos/" and "files/course-headers/".
      //
      // "files" was never declared in BlobContainerConstants, and FileFunction rejects an unknown
      // container name outright, so every upload from this screen came back 400 and no course ever
      // got an image. Declaring it is the fix.
      if (logoFile) {
        const uploadResponse = await uploadFile(
          'files',
          logoFile.name,
          logoFile,
          'course-logos',
        );
        if (uploadResponse && uploadResponse.success) {
          data.logoFileId = uploadResponse.data;
        }
      }

      if (headerImageFile) {
        const uploadResponse = await uploadFile(
          'files',
          headerImageFile.name,
          headerImageFile,
          'course-headers',
        );
        if (uploadResponse && uploadResponse.success) {
          data.headerImageFileId = uploadResponse.data;
        }
      }

      const created = await createCourse.mutateAsync(data);

      if (fromRequestId) {
        if (!created?.id) {
          // The course is saved either way; only the link back to the request is missing.
          showToast(
            'Course created, but the request could not be approved. Link it from the request.',
            'warning',
          );
          navigate(backTo);
          return;
        }

        try {
          await approveRequest.mutateAsync({
            id: fromRequestId,
            courseId: created.id,
          });
          showToast('Course created and request approved', 'success');
          navigate('/admin/course-requests');
        } catch (error) {
          console.error('Error approving Course Request:', error);
          showToast(
            'Course created, but the request could not be approved. Link it from the request.',
            'warning',
          );
          navigate(backTo);
        }
        return;
      }

      navigate('/admin/courses');
    } catch (error) {
      console.error('Error creating Course:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/courses"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.Course.Edit"
    >
      {fromRequestId && courseRequest && (
        <div className="mx-4 mb-4 mt-4 rounded-lg border border-primary/40 bg-primary/10 px-4 py-3 text-sm text-ink">
          <p className="font-semibold">
            Creating from a course request by {courseRequest.memberName}
          </p>
          {courseRequest.location && (
            <p className="mt-1">Town or area: {courseRequest.location}</p>
          )}
          {courseRequest.notes && (
            <p className="mt-1 whitespace-pre-line">
              Notes: {courseRequest.notes}
            </p>
          )}
        </div>
      )}

      {/* CourseForm reads initialValues once, on mount, so it waits for the request to load
          rather than opening empty and ignoring it when it arrives. */}
      {fromRequestId && isLoadingRequest ? (
        <p>Loading...</p>
      ) : (
        <CourseForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onCancel={() => navigate(backTo)}
          setLogoFile={setLogoFile}
          setHeaderImageFile={setHeaderImageFile}
          isLoading={isSubmitting}
        />
      )}
    </AdminLayout>
  );
};

export default CourseCreate;
